import { useState } from "react";
import { ViewHeader } from "./PortalShared";
import { DollarSign, Calculator } from "lucide-react";
import { Input } from "./ui/input";

function fmt(n) {
  if (!n && n !== 0) return "$0";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

const SPLITS = [
  { label: "Buyer's Agent Only",    buy: 100, sell: 0 },
  { label: "Seller's Agent Only",   buy: 0,   sell: 100 },
  { label: "50/50 Split",           buy: 50,  sell: 50 },
  { label: "60/40 (Buyer/Seller)",  buy: 60,  sell: 40 },
];

export default function CommissionCalculator() {
  const [form, setForm] = useState({
    sale_price: "", commission_pct: "3",
    buyer_side_pct: "50", split_preset: "50/50 Split",
    broker_split: "70", property_address: ""
  });
  const [result, setResult] = useState(null);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const applyPreset = (preset) => {
    const s = SPLITS.find(x => x.label === preset);
    if (s) f("buyer_side_pct", String(s.buy));
    f("split_preset", preset);
  };

  const calculate = () => {
    const price = parseFloat(form.sale_price) || 0;
    const commPct = parseFloat(form.commission_pct) || 0;
    const buyerSidePct = parseFloat(form.buyer_side_pct) || 50;
    const brokerSplit = parseFloat(form.broker_split) || 70;

    const totalComm = (price * commPct) / 100;
    const buyerSide = (totalComm * buyerSidePct) / 100;
    const sellerSide = totalComm - buyerSide;
    const agentNetBuyer = (buyerSide * brokerSplit) / 100;
    const agentNetSeller = (sellerSide * brokerSplit) / 100;
    const agentNet = agentNetBuyer + agentNetSeller;

    setResult({ price, commPct, totalComm, buyerSide, sellerSide, agentNet, brokerSplit, buyerSidePct });
  };

  return (
    <div>
      <ViewHeader title="Commission Calculator" subtitle="Estimate commissions for any real estate transaction" />
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
          <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold">Transaction Details</p>
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Property Address (optional)</label>
            <Input value={form.property_address} onChange={e => f("property_address", e.target.value)} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Sale Price</label>
            <div className="relative">
              <Input type="number" value={form.sale_price} onChange={e => f("sale_price", e.target.value)} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm pl-7" />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-xs">$</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Commission Rate (%)</label>
            <Input type="number" step="0.1" value={form.commission_pct} onChange={e => f("commission_pct", e.target.value)} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 block mb-2">Commission Split</label>
            <div className="grid grid-cols-2 gap-1.5">
              {SPLITS.map(s => (
                <button key={s.label} onClick={() => applyPreset(s.label)}
                  className={`text-[10px] py-1.5 px-2 border rounded transition-all ${form.split_preset === s.label ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]' : 'bg-white/[0.02] border-white/[0.06] text-white/40 hover:text-white'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Buyer-Side % of Total Commission</label>
            <Input type="number" value={form.buyer_side_pct} onChange={e => f("buyer_side_pct", e.target.value)} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
          </div>
          <div>
            <label className="text-[10px] text-white/40 block mb-1">Your Broker Split (%)</label>
            <Input type="number" value={form.broker_split} onChange={e => f("broker_split", e.target.value)} className="bg-white/[0.03] border-white/[0.08] text-white h-8 text-sm" />
          </div>
          <button onClick={calculate}
            className="w-full py-2.5 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold text-sm rounded transition-all flex items-center justify-center gap-2"
            data-testid="calculate-commission-btn">
            <Calculator className="w-4 h-4" /> Calculate Commission
          </button>
        </div>
        <div>
          {result ? (
            <div className="space-y-4">
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl p-5">
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-4">Commission Breakdown</p>
                <div className="text-center mb-5">
                  <p className="text-white/40 text-xs">Your Net Commission</p>
                  <p className="text-[#D4AF37] text-4xl font-bold mt-1">{fmt(result.agentNet)}</p>
                  <p className="text-white/30 text-xs mt-1">After {result.brokerSplit}% broker split</p>
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: "Sale Price", value: fmt(result.price), color: "text-white" },
                    { label: `Total Commission (${result.commPct}%)`, value: fmt(result.totalComm), color: "text-[#D4AF37]" },
                    { label: `Buyer-Side (${result.buyerSidePct}%)`, value: fmt(result.buyerSide), color: "text-blue-400" },
                    { label: `Seller-Side (${100 - result.buyerSidePct}%)`, value: fmt(result.sellerSide), color: "text-purple-400" },
                    { label: "Your Net (after broker split)", value: fmt(result.agentNet), color: "text-[#00FF88]", bold: true },
                  ].map(({ label, value, color, bold }) => (
                    <div key={label} className="flex items-center justify-between border-b border-white/[0.04] pb-2">
                      <span className="text-white/50 text-sm">{label}</span>
                      <span className={`${bold ? 'font-bold' : 'font-medium'} text-sm ${color}`}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Visual Split</p>
                {[
                  { label: `Buyer Side ${result.buyerSidePct}%`, pct: result.buyerSidePct, color: "bg-blue-500" },
                  { label: `Seller Side ${100-result.buyerSidePct}%`, pct: 100-result.buyerSidePct, color: "bg-purple-500" },
                ].map(({ label, pct, color }) => (
                  <div key={label} className="mb-2">
                    <div className="flex justify-between text-xs text-white/40 mb-1"><span>{label}</span></div>
                    <div className="h-3 bg-white/[0.04] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-20 bg-white/[0.01] border border-white/[0.05] rounded-xl">
              <DollarSign className="w-12 h-12 text-[#D4AF37]/30 mb-4" />
              <p className="text-white/30 text-sm">Enter transaction details to calculate commission</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
